import React, { useState, useEffect, useCallback } from 'react';
import {
  getTenants,
  getSimulcastTenants,
  getDeployBranches,
  startFullDeploy,
  getDeploySession,
  getDeployHistory,
  getPendingApprovals,
  approveDeployment,
  rejectDeployment,
} from '../../services/deploymentsApi';

const CONFIRMATION_PHRASE_STAGING = 'DEPLOY TO STAGING';
const CONFIRMATION_PHRASE_DIRECT  = 'DEPLOY TO PRODUCTION';
const POLL_INTERVAL_MS = 5000;

const StatusBadge = ({ status }) => {
  const classes = {
    pending: 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300',
    inProgress: 'bg-blue-100 text-blue-700 animate-pulse',
    succeeded: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  const label = {
    pending: 'Pending',
    inProgress: 'Running…',
    succeeded: 'Succeeded',
    failed: 'Failed',
  };
  const cls = classes[status] || classes.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status === 'inProgress' && (
        <span className="inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      )}
      {label[status] || status}
    </span>
  );
};

const PRODUCTS = [
  { key: 'backoffice', label: 'BackOffice', color: 'blue' },
  { key: 'simulcast',  label: 'Simulcast',  color: 'violet' },
];

const DeploymentTrigger = ({ onDeploymentTriggered }) => {
  const [product, setProduct] = useState('backoffice');
  const [backofficeTenants, setBackofficeTenants] = useState({ nonProd: [], prod: [] });
  const [simulcastTenants, setSimulcastTenants] = useState({ nonProd: [], prod: [] });
  const [branches, setBranches] = useState([]);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const tenantWrapperRef = React.useRef(null);
  const [selectedBranch, setSelectedBranch] = useState('development');
  const [branchSearch, setBranchSearch] = useState('');
  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
  const branchWrapperRef = React.useRef(null);
  const branchSearchInputRef = React.useRef(null);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [phase, setPhase] = useState('select'); // select | progress
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [deploymentMode, setDeploymentMode] = useState('staging'); // 'staging' | 'direct'
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [error, setError] = useState(null);

  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState({ type: null, text: null });
  const [actingApprovalId, setActingApprovalId] = useState(null);
  const [approvalComments, setApprovalComments] = useState({});

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  // Active tenant list based on selected product tab
  const tenants = product === 'simulcast' ? simulcastTenants : backofficeTenants;
  const selectedProdTenants = selectedTenants.filter(t => tenants.prod?.includes(t));
  const hasAnyProdTenant = selectedProdTenants.length > 0;
  const productMeta = PRODUCTS.find(p => p.key === product) || PRODUCTS[0];

  const refreshApprovals = useCallback(async () => {
    setApprovalsLoading(true);
    setApprovalMessage({ type: null, text: null });
    try {
      const list = await getPendingApprovals();
      setPendingApprovals(Array.isArray(list) ? list : []);
    } catch (err) {
      setPendingApprovals([]);
      setApprovalMessage({ type: 'error', text: err.message || 'Failed to load approvals' });
    } finally {
      setApprovalsLoading(false);
    }
  }, []);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await getDeployHistory(50);
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      // silently ignore
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshApprovals();
    refreshHistory();
  }, [refreshApprovals, refreshHistory]);

  // Poll approvals every 10 seconds so direct-to-production gates always surface promptly
  useEffect(() => {
    const t = setInterval(refreshApprovals, 10000);
    return () => clearInterval(t);
  }, [refreshApprovals]);

  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    Promise.all([getTenants(), getSimulcastTenants()])
      .then(([bo, sc]) => {
        if (cancelled) return;
        setBackofficeTenants({ nonProd: bo.nonProd || [], prod: bo.prod || [] });
        setSimulcastTenants({ nonProd: sc.nonProd || [], prod: sc.prod || [] });
      })
      .catch(() => {})
      .finally(() => !cancelled && setTenantsLoading(false));
    return () => { cancelled = true; };
  }, []); // intentionally run once on mount

  useEffect(() => {
    let cancelled = false;
    setBranchesLoading(true);
    getDeployBranches()
      .then(data => {
        if (!cancelled) {
          const arr = Array.isArray(data) ? data : [];
          setBranches(arr);
          const names = arr.map(b => b.name);
          setSelectedBranch(prev =>
            names.includes(prev) ? prev : names.includes('development') ? 'development' : names[0] || ''
          );
        }
      })
      .catch(() => !cancelled && setBranches([]))
      .finally(() => !cancelled && setBranchesLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Clear tenant selection and reset deployment mode when switching product
  useEffect(() => {
    setSelectedTenants([]);
    setDeploymentMode('staging');
  }, [product]);

  // Close tenant dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (tenantWrapperRef.current && !tenantWrapperRef.current.contains(e.target)) {
        setTenantDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close branch dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (branchWrapperRef.current && !branchWrapperRef.current.contains(e.target)) {
        setBranchDropdownOpen(false);
        setBranchSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus the search input when dropdown opens
  useEffect(() => {
    if (branchDropdownOpen && branchSearchInputRef.current) {
      branchSearchInputRef.current.focus();
    }
  }, [branchDropdownOpen]);

  useEffect(() => {
    if (!sessionId || phase !== 'progress') return;
    const t = setInterval(async () => {
      try {
        const s = await getDeploySession(sessionId);
        setSession(s);
        // Refresh approvals during releasing phase so they appear inline
        if (s?.overallStatus === 'releasing') {
          refreshApprovals();
        }
        if (s?.overallStatus === 'completed' || s?.overallStatus === 'failed' || s?.overallStatus === 'partiallyFailed') {
          setTriggering(false);
          onDeploymentTriggered?.();
          refreshApprovals();
          refreshHistory();
        }
      } catch {
        // ignore poll errors
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [sessionId, phase, onDeploymentTriggered, refreshApprovals, refreshHistory]);

  const handleApprove = async (approvalId, comment) => {
    setActingApprovalId(approvalId);
    setApprovalMessage({ type: null, text: null });
    try {
      await approveDeployment(approvalId, comment);
      setApprovalMessage({ type: 'success', text: 'Approved. Deployment will proceed.' });
      setApprovalComments(prev => ({ ...prev, [approvalId]: '' }));
    } catch (err) {
      setApprovalMessage({ type: 'error', text: err.message || 'Failed to approve' });
    } finally {
      setActingApprovalId(null);
      // Always refresh so stale/completed approvals disappear immediately
      await refreshApprovals();
    }
  };

  const handleReject = async (approvalId, comment) => {
    setActingApprovalId(approvalId);
    setApprovalMessage({ type: null, text: null });
    try {
      await rejectDeployment(approvalId, comment);
      setApprovalMessage({ type: 'success', text: 'Rejected.' });
      setApprovalComments(prev => ({ ...prev, [approvalId]: '' }));
    } catch (err) {
      setApprovalMessage({ type: 'error', text: err.message || 'Failed to reject' });
    } finally {
      setActingApprovalId(null);
      // Always refresh so stale/completed approvals disappear immediately
      await refreshApprovals();
    }
  };

  const executeDeploy = useCallback(async () => {
    setShowConfirmModal(false);
    setConfirmInput('');
    setError(null);
    setTriggering(true);
    setPhase('progress');
    setSession(null);
    try {
      const { sessionId: id } = await startFullDeploy(product, selectedTenants, selectedBranch, deploymentMode);
      setSessionId(id);
      const s = await getDeploySession(id);
      setSession(s);
    } catch (err) {
      setError(err.message || 'Failed to start deployment');
      setTriggering(false);
      setPhase('select');
    }
  }, [product, selectedTenants, selectedBranch, deploymentMode]);

  const confirmationPhrase = deploymentMode === 'direct'
    ? CONFIRMATION_PHRASE_DIRECT
    : CONFIRMATION_PHRASE_STAGING;

  const handleDeployClick = () => {
    if (hasAnyProdTenant) {
      setShowConfirmModal(true);
      setConfirmInput('');
    } else {
      executeDeploy();
    }
  };

  const toggleTenant = (t) => {
    setSelectedTenants(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
    );
  };

  const toggleGroup = (group) => {
    const allSelected = group.every(t => selectedTenants.includes(t));
    if (allSelected) {
      setSelectedTenants(prev => prev.filter(t => !group.includes(t)));
    } else {
      setSelectedTenants(prev => [...new Set([...prev, ...group])]);
    }
  };

  const canDeploy = selectedTenants.length > 0 && selectedBranch && !triggering;

  const formatApprovalDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

  const overallStatusClass = (status) => {
    switch (status) {
      case 'completed':       return 'bg-green-100 dark:bg-green-900 text-green-700';
      case 'partiallyFailed': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700';
      case 'failed':          return 'bg-red-100 dark:bg-red-900 text-red-700';
      case 'releasing':       return 'bg-blue-100 dark:bg-blue-900 text-blue-700 animate-pulse';
      case 'building':        return 'bg-blue-100 dark:bg-blue-900 text-blue-700 animate-pulse';
      default:                return 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400';
    }
  };

  const overallStatusLabel = (status) => {
    switch (status) {
      case 'completed':       return 'Completed';
      case 'partiallyFailed': return 'Partial';
      case 'failed':          return 'Failed';
      case 'releasing':       return 'Releasing…';
      case 'building':        return 'Building…';
      default:                return capitalize(status);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString();
    } catch { return dateStr; }
  };

  return (
    <div className="deployment-trigger-container">
      <div className="trigger-form">
        {/* Pending approvals */}
        <div className="approvals-section trigger-section">
          <div className="flex items-center justify-between mb-2">
            <h3 className="trigger-section-title approvals-section-title mb-0">Pending approvals</h3>
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline disabled:opacity-50"
              onClick={refreshApprovals}
              disabled={approvalsLoading}
            >
              {approvalsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {approvalMessage.text && (
            <p
              className={
                approvalMessage.type === 'error'
                  ? 'text-red-600 text-sm mb-2'
                  : approvalMessage.type === 'success'
                    ? 'text-green-600 text-sm mb-2'
                    : 'text-gray-600 dark:text-gray-400 text-sm mb-2'
              }
            >
              {approvalMessage.text}
            </p>
          )}
          {!approvalsLoading && pendingApprovals.length === 0 && (
            <p className="text-gray-500 dark:text-slate-400 text-sm">No pending approvals.</p>
          )}
          {pendingApprovals.length > 0 && (
            <ul className="approvals-list space-y-3">
              {pendingApprovals.map(a => (
                <li key={a.id} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  {/* Header row */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {a.pipelineName && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-semibold">
                        {a.pipelineName}
                      </span>
                    )}
                    {a.runName && (
                      a.runUrl ? (
                        <a
                          href={a.runUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2 py-0.5 bg-white border border-amber-200 text-amber-700 rounded text-xs font-mono hover:bg-amber-50"
                          title="View run in Azure DevOps"
                        >
                          #{a.runName} ↗
                        </a>
                      ) : (
                        <span className="px-2 py-0.5 bg-white border border-amber-200 text-amber-700 rounded text-xs font-mono">
                          #{a.runName}
                        </span>
                      )
                    )}
                    <span className="text-xs text-gray-500 dark:text-slate-400">{formatApprovalDate(a.createdOn)}</span>
                    {a.instructions && (
                      <span className="text-xs text-gray-600 dark:text-gray-400 italic">{a.instructions}</span>
                    )}
                  </div>

                  <p className="text-xs text-amber-800 mb-3">
                    This pipeline is waiting for your approval before proceeding to the next stage.
                    Approving will allow the deployment to continue.
                  </p>

                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      className="flex-1 px-2 py-1.5 border rounded text-sm"
                      placeholder="Comment (optional)"
                      value={approvalComments[a.id] ?? ''}
                      onChange={e =>
                        setApprovalComments(prev => ({ ...prev, [a.id]: e.target.value }))
                      }
                    />
                    <button
                      type="button"
                      className="approvals-btn-approve"
                      disabled={actingApprovalId !== null}
                      onClick={() => handleApprove(a.id, approvalComments[a.id])}
                    >
                      {actingApprovalId === a.id ? '…' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      className="approvals-btn-reject"
                      disabled={actingApprovalId !== null}
                      onClick={() => handleReject(a.id, approvalComments[a.id])}
                    >
                      {actingApprovalId === a.id ? '…' : 'Reject'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {phase === 'select' && (
          <div className="trigger-section space-y-4">
            <h3 className="trigger-section-title">Full Deploy</h3>

            {/* Product tabs */}
            <div className="flex gap-1 p-1 bg-gray-100 dark:bg-slate-800 rounded-lg w-fit">
              {PRODUCTS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => { setProduct(p.key); setError(null); }}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                    product === p.key
                      ? p.key === 'simulcast'
                        ? 'bg-white dark:bg-slate-900 text-violet-700 shadow-sm'
                        : 'bg-white dark:bg-slate-900 text-blue-700 shadow-sm'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400">
              {product === 'backoffice'
                ? 'Deploy all 7 BackOffice pipelines (BackOffice, Storefront, BlackBird API, Company API, Contacts API, Inventory API, OnlineAuction API).'
                : 'Deploy all 3 Simulcast pipelines (Simulcast API, Simulcast Auction, Simulcast Auction Engine).'}
            </p>
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-950 text-red-700 rounded-lg text-sm">{error}</div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="relative" ref={tenantWrapperRef}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tenants</label>
                <button
                  type="button"
                  className="trigger-select w-full flex items-center justify-between gap-2 text-left disabled:opacity-60"
                  disabled={tenantsLoading}
                  onClick={() => setTenantDropdownOpen(o => !o)}
                >
                  <span className={`truncate text-sm ${selectedTenants.length > 0 ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-slate-500'}`}>
                    {tenantsLoading
                      ? 'Loading tenants...'
                      : selectedTenants.length === 0
                        ? 'Select tenants...'
                        : selectedTenants.length === 1
                          ? capitalize(selectedTenants[0])
                          : `${selectedTenants.length} tenants selected`}
                  </span>
                  <svg className={`w-4 h-4 text-gray-400 dark:text-slate-500 shrink-0 transition-transform ${tenantDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06z" clipRule="evenodd" />
                  </svg>
                </button>

                {tenantDropdownOpen && (
                  <div className="absolute z-30 mt-1 w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-gray-100 dark:border-slate-800 flex gap-2">
                      <button type="button" className="px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                        onClick={() => setSelectedTenants([...(tenants.nonProd || []), ...(tenants.prod || [])])}>
                        All
                      </button>
                      <button type="button" className="px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                        onClick={() => setSelectedTenants([])}>
                        Clear
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {tenants.nonProd?.length > 0 && (
                        <>
                          <div className="flex items-center justify-between px-3 pt-2 pb-1">
                            <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Non-Production</span>
                            <button type="button" className="text-xs text-blue-600 hover:underline"
                              onClick={() => toggleGroup(tenants.nonProd)}>
                              {tenants.nonProd.every(t => selectedTenants.includes(t)) ? 'Deselect all' : 'Select all'}
                            </button>
                          </div>
                          {tenants.nonProd.map(t => (
                            <div key={t} className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer" onClick={() => toggleTenant(t)}>
                              <input type="checkbox" checked={selectedTenants.includes(t)} readOnly className="rounded w-3.5 h-3.5 accent-blue-600" />
                              <span className="text-sm">{capitalize(t)}</span>
                            </div>
                          ))}
                        </>
                      )}
                      {tenants.prod?.length > 0 && (
                        <>
                          <div className="flex items-center justify-between px-3 pt-3 pb-1 border-t border-gray-100 dark:border-slate-800 mt-1">
                            <span className="text-xs font-semibold text-red-500 uppercase tracking-wide">Production</span>
                            <button type="button" className="text-xs text-red-600 hover:underline"
                              onClick={() => toggleGroup(tenants.prod)}>
                              {tenants.prod.every(t => selectedTenants.includes(t)) ? 'Deselect all' : 'Select all'}
                            </button>
                          </div>
                          {tenants.prod.map(t => (
                            <div key={t} className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-red-50 cursor-pointer" onClick={() => toggleTenant(t)}>
                              <input type="checkbox" checked={selectedTenants.includes(t)} readOnly className="rounded w-3.5 h-3.5 accent-red-600" />
                              <span className="text-sm">{capitalize(t)}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {selectedTenants.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {selectedTenants.map(t => (
                      <span key={t} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        tenants.prod?.includes(t) ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {capitalize(t)}
                        <button type="button" className="hover:opacity-70" onClick={() => toggleTenant(t)}>
                          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22z" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative" ref={branchWrapperRef}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Branch</label>

                {/* Trigger button — shows selected branch */}
                <button
                  type="button"
                  className="trigger-select w-full flex items-center justify-between gap-2 text-left disabled:opacity-60"
                  disabled={branchesLoading}
                  onClick={() => {
                    setBranchDropdownOpen(o => !o);
                    setBranchSearch('');
                  }}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {/* Branch icon */}
                    <svg className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 shrink-0" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M11.75 2.5a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0zm.75 1.729a2.25 2.25 0 1 1-1.5 0V5.5a3 3 0 0 1-3 3H6.5a1.5 1.5 0 0 0-1.5 1.5v.771a2.25 2.25 0 1 1-1.5 0V6.729a2.25 2.25 0 1 1 1.5 0V8.5A1.5 1.5 0 0 0 6.5 10H8a3 3 0 0 0 3-3V4.229z"/>
                    </svg>
                    <span className={`truncate text-sm ${selectedBranch ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-slate-500'}`}>
                      {branchesLoading ? 'Loading branches…' : selectedBranch || 'Select branch'}
                    </span>
                  </span>
                  <svg className={`w-4 h-4 text-gray-400 dark:text-slate-500 shrink-0 transition-transform ${branchDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Dropdown panel */}
                {branchDropdownOpen && (
                  <div className="absolute z-30 mt-1 w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-gray-100 dark:border-slate-800">
                      <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-700 rounded-md">
                        <svg className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9z" clipRule="evenodd" />
                        </svg>
                        <input
                          ref={branchSearchInputRef}
                          type="text"
                          className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400 dark:placeholder-slate-500 dark:text-gray-100"
                          placeholder="Search branches…"
                          value={branchSearch}
                          onChange={e => setBranchSearch(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Escape') {
                              setBranchDropdownOpen(false);
                              setBranchSearch('');
                            }
                          }}
                        />
                        {branchSearch && (
                          <button type="button" onClick={() => setBranchSearch('')} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-gray-300">
                            <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Branch list */}
                    <ul className="max-h-56 overflow-y-auto">
                      {(() => {
                        const q = branchSearch.trim().toLowerCase();
                        const filtered = q
                          ? branches.filter(b => b.name.toLowerCase().includes(q))
                          : branches;
                        if (filtered.length === 0) {
                          return (
                            <li className="px-4 py-3 text-sm text-gray-400 dark:text-slate-500 text-center">
                              No branches match "{branchSearch}"
                            </li>
                          );
                        }
                        return filtered.map(b => (
                          <li
                            key={b.name}
                            className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm select-none
                              ${b.name === selectedBranch
                                ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 font-medium'
                                : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800'}`}
                            onMouseDown={e => {
                              e.preventDefault();
                              setSelectedBranch(b.name);
                              setBranchDropdownOpen(false);
                              setBranchSearch('');
                            }}
                          >
                            <svg className="w-3.5 h-3.5 shrink-0 text-current opacity-60" viewBox="0 0 16 16" fill="currentColor">
                              <path d="M11.75 2.5a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0zm.75 1.729a2.25 2.25 0 1 1-1.5 0V5.5a3 3 0 0 1-3 3H6.5a1.5 1.5 0 0 0-1.5 1.5v.771a2.25 2.25 0 1 1-1.5 0V6.729a2.25 2.25 0 1 1 1.5 0V8.5A1.5 1.5 0 0 0 6.5 10H8a3 3 0 0 0 3-3V4.229z"/>
                            </svg>
                            <span className="truncate flex-1">{b.name}</span>
                            {b.isDefault && (
                              <span className="text-xs text-gray-400 dark:text-slate-500 shrink-0">default</span>
                            )}
                            {b.name === selectedBranch && (
                              <svg className="w-4 h-4 shrink-0 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143z" clipRule="evenodd" />
                              </svg>
                            )}
                          </li>
                        ));
                      })()}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            {/* Deployment slot choice — only shown when prod tenants are selected */}
            {hasAnyProdTenant && (
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setDeploymentMode('staging')}
                  className={`flex flex-col gap-1 p-3 rounded-lg border-2 text-left transition-all ${
                    deploymentMode === 'staging'
                      ? 'border-green-500 bg-green-50 dark:bg-green-950'
                      : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-gray-300 dark:hover:border-slate-600'
                  }`}
                >
                  <span className={`text-sm font-semibold ${deploymentMode === 'staging' ? 'text-green-700' : 'text-gray-700 dark:text-gray-300'}`}>
                    ✅ Staging Slot
                  </span>
                  <span className="text-xs text-gray-500 dark:text-slate-400 leading-snug">
                    Deploys to staging first. ADO approval required to swap to production.
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setDeploymentMode('direct')}
                  className={`flex flex-col gap-1 p-3 rounded-lg border-2 text-left transition-all ${
                    deploymentMode === 'direct'
                      ? 'border-red-500 bg-red-50 dark:bg-red-950'
                      : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-gray-300 dark:hover:border-slate-600'
                  }`}
                >
                  <span className={`text-sm font-semibold ${deploymentMode === 'direct' ? 'text-red-700' : 'text-gray-700 dark:text-gray-300'}`}>
                    🚨 Production Slot — Direct
                  </span>
                  <span className="text-xs text-gray-500 dark:text-slate-400 leading-snug">
                    Bypasses staging. Goes directly to live traffic. Use with caution.
                  </span>
                </button>
              </div>
            )}

            <button
              type="button"
              className={`w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50 ${
                hasAnyProdTenant && deploymentMode === 'direct'
                  ? 'bg-red-600 hover:bg-red-700'
                  : hasAnyProdTenant
                    ? 'bg-green-600 hover:bg-green-700'
                    : product === 'simulcast'
                      ? 'bg-violet-600 hover:bg-violet-700'
                      : 'bg-blue-600 hover:bg-blue-700'
              }`}
              onClick={handleDeployClick}
              disabled={!canDeploy}
            >
              Deploy {productMeta.label}{selectedTenants.length > 0 ? ` (${selectedTenants.length} tenant${selectedTenants.length > 1 ? 's' : ''})` : ''}
            </button>
          </div>
        )}

        {phase === 'progress' && session && (
          <div className="trigger-section">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                session.product === 'simulcast' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {session.product === 'simulcast' ? 'Simulcast' : 'BackOffice'}
              </span>
              {/* Deployment mode badge */}
              {session.deploymentMode === 'direct' ? (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                  🚨 Direct to Production
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                  ✅ Staging Slot
                </span>
              )}
              <h3 className="trigger-section-title mb-0">
                {session.overallStatus === 'building' && 'Building…'}
                {session.overallStatus === 'releasing' && 'Releasing…'}
                {(session.overallStatus === 'completed' || session.overallStatus === 'partiallyFailed' || session.overallStatus === 'failed') && 'Deployment Complete'}
              </h3>
              <span className="text-xs text-gray-500 dark:text-slate-400">
                {(session.tenants || []).map(capitalize).join(', ')}
              </span>
            </div>

            {/* Info banner about staging → prod flow (only for staging slot deployments) */}
            {session.overallStatus === 'releasing' && session.deploymentMode !== 'direct' && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg mb-3 text-sm text-blue-800 dark:text-blue-200">
                <strong>Release flow:</strong> Pipelines deploy to the <strong>staging slot</strong> first.
                Once staging completes, an <strong>approval</strong> is required before swapping to the <strong>production slot</strong>.
                Check the "Pending approvals" section above to approve or reject.
              </div>
            )}
            {/* Info banner for direct-to-production deployments */}
            {session.overallStatus === 'releasing' && session.deploymentMode === 'direct' && (
              <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-3 text-sm text-red-800 dark:text-red-200">
                <strong>Direct to production:</strong> Pipelines are deploying <strong>directly to the production slot</strong>.
                Live traffic is being updated now.
              </div>
            )}

            {/* Inline approval banner when approvals are pending during staging slot release */}
            {session.overallStatus === 'releasing' && session.deploymentMode !== 'direct' && pendingApprovals.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg mb-3">
                <p className="text-sm font-semibold text-amber-800 mb-1">
                  {pendingApprovals.length} approval{pendingApprovals.length > 1 ? 's' : ''} waiting — production slot deployment paused
                </p>
                <p className="text-xs text-amber-700">
                  Scroll up to the "Pending approvals" section to approve or reject. Approving will push the staging deployment to the production slot.
                </p>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700">
                    <th className="text-left py-2 font-medium">Pipeline</th>
                    <th className="text-left py-2 font-medium">Type</th>
                    <th className="text-left py-2 font-medium">Build</th>
                    <th className="text-left py-2 font-medium">Release</th>
                  </tr>
                </thead>
                <tbody>
                  {(session.pipelines || []).map((p, i) => {
                    // Check if this release pipeline has a pending approval
                    const hasApproval = p.releaseStatus === 'inProgress' && pendingApprovals.some(
                      a => a.pipelineId === p.releaseRunId || (a.pipelineName && p.name && a.pipelineName.toLowerCase().includes(p.slug))
                    );
                    return (
                    <tr key={p.slug || i} className="border-b border-gray-100 dark:border-slate-800">
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${p.type === 'frontend' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'}`}>
                          {capitalize(p.type)}
                        </span>
                      </td>
                      <td className="py-2">
                        <span className="flex items-center gap-2">
                          <StatusBadge status={p.buildStatus} />
                          {p.buildUrl && (
                            <a href={p.buildUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                              View
                            </a>
                          )}
                        </span>
                        {p.buildStatus === 'failed' && p.errorMessage && (
                          <p className="text-xs text-red-500 mt-0.5 truncate max-w-[200px]" title={p.errorMessage}>{p.errorMessage}</p>
                        )}
                      </td>
                      <td className="py-2">
                        <span className="flex items-center gap-2">
                          {hasApproval ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                              <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9z" clipRule="evenodd" />
                              </svg>
                              Awaiting Approval
                            </span>
                          ) : (
                            <StatusBadge status={p.releaseStatus} />
                          )}
                          {p.releaseUrl && (
                            <a href={p.releaseUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                              View
                            </a>
                          )}
                        </span>
                        {p.releaseStatus === 'failed' && p.errorMessage && (
                          <p className="text-xs text-red-500 mt-0.5 truncate max-w-[200px]" title={p.errorMessage}>{p.errorMessage}</p>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(session.overallStatus === 'completed' || session.overallStatus === 'partiallyFailed' || session.overallStatus === 'failed') && (
              <button
                type="button"
                className="mt-4 px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 font-medium"
                onClick={() => {
                  setPhase('select');
                  setSessionId(null);
                  setSession(null);
                }}
              >
                New Deployment
              </button>
            )}
          </div>
        )}
      </div>

        {/* Deployment History */}
        <div className="trigger-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="trigger-section-title mb-0">Deployment History</h3>
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline disabled:opacity-50"
              onClick={refreshHistory}
              disabled={historyLoading}
            >
              {historyLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {!historyLoading && history.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-slate-400">No deployments yet.</p>
          )}
          {history.length > 0 && (
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.sessionId} className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800 text-left"
                    onClick={() => setExpandedHistoryId(prev => prev === h.sessionId ? null : h.sessionId)}
                  >
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${overallStatusClass(h.overallStatus)}`}>
                      {overallStatusLabel(h.overallStatus)}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                      h.product === 'simulcast' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {h.product === 'simulcast' ? 'Simulcast' : 'BackOffice'}
                    </span>
                    {h.deploymentMode === 'direct' ? (
                      <span className="px-2 py-0.5 rounded text-xs font-medium shrink-0 bg-red-100 text-red-700">
                        🚨 Direct
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-xs font-medium shrink-0 bg-green-100 text-green-700">
                        ✅ Staging
                      </span>
                    )}
                    <span className="font-medium text-sm text-gray-800 dark:text-gray-200 shrink-0">
                      {Array.isArray(h.tenants)
                        ? h.tenants.length <= 3
                          ? h.tenants.map(capitalize).join(', ')
                          : `${h.tenants.length} tenants`
                        : capitalize(h.tenant || '')}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-slate-400 font-mono truncate">{h.branch}</span>
                    <span className="ml-auto text-xs text-gray-400 dark:text-slate-500 shrink-0">{formatDate(h.startedAt)}</span>
                    <span className={`text-gray-400 dark:text-slate-500 text-xs shrink-0 ${expandedHistoryId === h.sessionId ? 'rotate-180' : ''} transition-transform`}>▼</span>
                  </button>
                  {expandedHistoryId === h.sessionId && (
                    <div className="px-4 pb-3 border-t border-gray-100 dark:border-slate-800">
                      <div className="overflow-x-auto mt-2">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100 dark:border-slate-800">
                              <th className="text-left py-1.5 font-medium text-gray-600 dark:text-gray-400">Pipeline</th>
                              <th className="text-left py-1.5 font-medium text-gray-600 dark:text-gray-400">Type</th>
                              <th className="text-left py-1.5 font-medium text-gray-600 dark:text-gray-400">Build</th>
                              <th className="text-left py-1.5 font-medium text-gray-600 dark:text-gray-400">Release</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(h.pipelines || []).map((p, i) => (
                              <tr key={p.slug || i} className="border-b border-gray-50">
                                <td className="py-1.5 font-medium">{p.name}</td>
                                <td className="py-1.5">
                                  <span className={`px-2 py-0.5 rounded text-xs ${p.type === 'frontend' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'}`}>
                                    {capitalize(p.type)}
                                  </span>
                                </td>
                                <td className="py-1.5">
                                  <span className="flex items-center gap-2">
                                    <StatusBadge status={p.buildStatus} />
                                    {p.buildUrl && (
                                      <a href={p.buildUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View</a>
                                    )}
                                  </span>
                                </td>
                                <td className="py-1.5">
                                  <span className="flex items-center gap-2">
                                    <StatusBadge status={p.releaseStatus} />
                                    {p.releaseUrl && (
                                      <a href={p.releaseUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View</a>
                                    )}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      {showConfirmModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
              {deploymentMode === 'direct'
                ? '🚨 Confirm Direct Production Deployment'
                : '✅ Confirm Staging Slot Deployment'}
            </h3>

            {deploymentMode === 'direct' ? (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-800 dark:text-red-200">
                <strong>Warning:</strong> This will deploy <strong>directly to the production slot</strong> for{' '}
                <strong>{selectedProdTenants.map(capitalize).join(', ')}</strong>, bypassing staging.
                Live traffic will be affected immediately.
                {selectedTenants.length > selectedProdTenants.length && (
                  <span> Non-prod tenants will also deploy: {selectedTenants.filter(t => !tenants.prod?.includes(t)).map(capitalize).join(', ')}.</span>
                )}
              </div>
            ) : (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-200">
                <strong>Safe:</strong> This will deploy to the <strong>staging slot</strong> for{' '}
                <strong>{selectedProdTenants.map(capitalize).join(', ')}</strong>.
                An ADO approval gate will appear in "Pending Approvals" before production traffic switches.
                {selectedTenants.length > selectedProdTenants.length && (
                  <span> Non-prod tenants will also deploy: {selectedTenants.filter(t => !tenants.prod?.includes(t)).map(capitalize).join(', ')}.</span>
                )}
              </div>
            )}

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Type <strong>{confirmationPhrase}</strong> to confirm:
            </p>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg mb-4"
              value={confirmInput}
              onChange={e => setConfirmInput(e.target.value)}
              placeholder={confirmationPhrase}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  deploymentMode === 'direct'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                disabled={confirmInput !== confirmationPhrase}
                onClick={executeDeploy}
              >
                Confirm Deploy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeploymentTrigger;
